import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { useRealtime } from '../RealtimeContext';
import { NodeConnection, NodeError } from './nodeClient';
import {
  PROTOCOL, cleanInfo, cleanMember, cleanMessage, cleanStructure, cleanUnread, cleanUser, cleanUsers, cleanRole,
} from './format';

// Community servers the user joined: the list (server rail) comes from the Core, everything else
// from each server (BLOBL Node) directly.
//
// WebSockets: only the open server and the 5 most recently used ones keep a live connection; it is
// closed after 30 minutes without looking at the server. The others are checked over REST (unread
// state) at start, when the window gets focus (at most once a minute) and every 5 minutes.
// So on servers "online" means "connected to this server right now", and mentions on a server
// without a socket show up with a delay of up to 5 minutes.

const SOCKETS_MAX = 6;
const SOCKET_IDLE_MS = 30 * 60 * 1000;
const POLL_MS = 5 * 60 * 1000;
const FOCUS_POLL_MIN_MS = 60 * 1000;
const START_STAGGER_MS = 150;
const TYPING_TIMEOUT_MS = 8000;

const coreHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const validDate = (v) => (typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : null);
const REMOVED_TEXT = {
  kicked: 'You were removed from this server.',
  banned: 'You were banned from this server.',
};

const EMPTY = {
  status: 'loading', // 'loading' | 'ready' | 'error'
  error: null,       // { kind, message } with kind 'unavailable' | 'not_member' | 'blocked' | 'offline' | 'outdated' | 'error'
  info: null,
  me: null,
  ownerId: null,
  openReports: null, // only for moderators and above
  categories: [],
  channels: [],
  unread: {},
  members: null,     // loaded on demand
  users: {},         // profile cache (authors, mentions)
  presence: {},
  typing: {},
  socket: 'disconnected',
};

const ServersContext = createContext(null);

export function ServersProvider({ children }) {
  const { user } = useAuth();
  const meId = user?.id;
  const [servers, setServers] = useState([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [state, setState] = useState({});
  const [blockedIds, setBlockedIds] = useState(() => new Set());

  const conns = useRef(new Map());
  const serversRef = useRef([]);
  const stateRef = useRef({});
  const listeners = useRef(new Map());
  const mru = useRef([]);           // node ids with a socket, most recent first
  const lastUsed = useRef(new Map());
  const lastPoll = useRef(0);
  const active = useRef(null);      // { nodeId, channelId } of the open channel
  const typingTimers = useRef({});
  const unreadReload = useRef({});
  const statusRef = useRef('online');
  const meRef = useRef(meId);
  meRef.current = meId;
  serversRef.current = servers;
  stateRef.current = state;

  const update = useCallback((nodeId, fn) => {
    setState(prev => ({ ...prev, [nodeId]: fn(prev[nodeId] || EMPTY) }));
  }, []);

  const dispatch = useCallback((nodeId, event, data) => {
    listeners.current.get(`${nodeId}:${event}`)?.forEach(h => h(data));
  }, []);

  /* ── Connections ── */

  const handleEventRef = useRef(null);

  // Connection for a server from the Core list, or for a node the Core just described (invite page)
  const connFor = useCallback((node) => {
    if (!node?.id || !node.url) return null;
    let c = conns.current.get(node.id);
    if (c && c.node.url !== node.url) { c.disconnect(); c = null; }
    if (!c) {
      c = new NodeConnection({ id: node.id, url: node.url }, {
        onEvent: (event, data) => handleEventRef.current?.(node.id, event, data),
        onSocketState: (s) => update(node.id, st => (st.socket === s ? st : { ...st, socket: s })),
      });
      c.status = statusRef.current;
      conns.current.set(node.id, c);
    }
    return c;
  }, [update]);

  const conn = useCallback((nodeId) => {
    const node = serversRef.current.find(s => s.id === nodeId);
    return node && node.status === 'approved' ? connFor(node) : null;
  }, [connFor]);

  const setError = useCallback((nodeId, err) => {
    const kind = err instanceof NodeError ? err.kind : 'error';
    update(nodeId, st => ({ ...st, status: 'error', error: { kind, message: err.message } }));
    if (kind === 'not_member' || kind === 'blocked' || kind === 'unavailable') conns.current.get(nodeId)?.disconnect();
  }, [update]);

  /* ── Loading ── */

  const loadServer = useCallback(async (nodeId) => {
    const node = serversRef.current.find(s => s.id === nodeId);
    if (!node) return;
    if (node.status === 'blocked') {
      update(nodeId, st => ({ ...st, status: 'error', error: { kind: 'blocked', message: 'This server was blocked by BLOBL' } }));
      return;
    }
    if (node.protocol_supported === false) {
      update(nodeId, st => ({ ...st, status: 'error', error: { kind: 'outdated', message: 'This server runs an outdated version of BLOBL Node. Ask the host to update it.' } }));
      return;
    }
    const c = conn(nodeId);
    if (!c) return;
    try {
      const d = await c.get('/api/server');
      if (Number(d?.protocol) !== PROTOCOL) {
        update(nodeId, st => ({ ...st, status: 'error', error: { kind: 'outdated', message: 'This server runs an outdated version of BLOBL Node. Ask the host to update it.' } }));
        return;
      }
      update(nodeId, st => ({
        ...st,
        status: 'ready',
        error: null,
        info: cleanInfo(d),
        me: { id: Number(d.me?.id), role: cleanRole(d.me?.role), timeoutUntil: validDate(d.me?.timeout_until) },
        ownerId: Number(d.owner_id) || null,
        openReports: Number.isFinite(Number(d.open_reports)) && d.open_reports !== null ? Number(d.open_reports) : null,
        ...cleanStructure(d),
        unread: cleanUnread(d.unread),
      }));
    } catch (err) {
      setError(nodeId, err);
    }
  }, [conn, update, setError]);

  const loadUnread = useCallback(async (nodeId) => {
    const c = conn(nodeId);
    if (!c || stateRef.current[nodeId]?.status !== 'ready') return;
    try {
      const d = await c.get('/api/unread');
      update(nodeId, st => ({ ...st, unread: cleanUnread(d.channels) }));
    } catch (err) {
      if (err.kind === 'not_member' || err.kind === 'blocked' || err.kind === 'unavailable') setError(nodeId, err);
    }
  }, [conn, update, setError]);

  const ensureMembers = useCallback(async (nodeId, force = false) => {
    const c = conn(nodeId);
    if (!c || (!force && stateRef.current[nodeId]?.members)) return;
    try {
      const list = (await c.get('/api/members')).map(cleanMember).filter(Boolean);
      update(nodeId, st => ({ ...st, members: list, users: { ...st.users, ...Object.fromEntries(list.map(m => [m.id, cleanUser(m)])) } }));
    } catch {}
  }, [conn, update]);

  const loadList = useCallback(async () => {
    try {
      const res = await axios.get('/api/users/me/servers', { headers: coreHeaders() });
      const list = (Array.isArray(res.data) ? res.data : []).filter(s => typeof s?.id === 'string');
      setServers(list);
    } catch {}
    setListLoaded(true);
  }, []);

  const loadBlocks = useCallback(async () => {
    try {
      const res = await axios.get('/api/users/me/blocks', { headers: coreHeaders() });
      setBlockedIds(new Set(res.data.map(u => u.id)));
    } catch {}
  }, []);

  /* ── Sockets: the open server plus the most recently used ones ── */

  const touch = useCallback((nodeId) => {
    const c = conn(nodeId);
    if (!c || stateRef.current[nodeId]?.status === 'error') return;
    lastUsed.current.set(nodeId, Date.now());
    mru.current = [nodeId, ...mru.current.filter(id => id !== nodeId)];
    c.connect();
    for (const id of mru.current.slice(SOCKETS_MAX)) conns.current.get(id)?.disconnect();
    mru.current = mru.current.slice(0, SOCKETS_MAX);
  }, [conn]);

  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      for (const id of [...mru.current]) {
        if (active.current?.nodeId === id) continue;
        if (now - (lastUsed.current.get(id) || 0) > SOCKET_IDLE_MS) {
          conns.current.get(id)?.disconnect();
          mru.current = mru.current.filter(x => x !== id);
        }
      }
    }, 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Unread state for servers without a socket
  const pollUnread = useCallback(() => {
    lastPoll.current = Date.now();
    serversRef.current.forEach((s, i) => {
      if (conns.current.get(s.id)?.connected) return;
      setTimeout(() => loadUnread(s.id), i * START_STAGGER_MS);
    });
  }, [loadUnread]);

  useEffect(() => {
    const t = setInterval(pollUnread, POLL_MS);
    const onFocus = () => { if (Date.now() - lastPoll.current > FOCUS_POLL_MIN_MS) pollUnread(); };
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [pollUnread]);

  /* ── Start, list changes, sign-out ── */

  useEffect(() => {
    if (!meId) return undefined;
    loadList();
    loadBlocks();
    const all = conns.current;
    return () => {
      for (const c of all.values()) c.disconnect();
      all.clear();
      mru.current = [];
      setServers([]);
      setState({});
      setListLoaded(false);
    };
  }, [meId, loadList, loadBlocks]);

  // New servers in the list get loaded; removed ones are disconnected
  const known = useRef(new Set());
  useEffect(() => {
    const ids = new Set(servers.map(s => s.id));
    let i = 0;
    for (const s of servers) {
      if (known.current.has(s.id)) continue;
      const id = s.id;
      setTimeout(() => loadServer(id), (i++) * START_STAGGER_MS);
    }
    for (const id of known.current) {
      if (ids.has(id)) continue;
      conns.current.get(id)?.disconnect();
      conns.current.delete(id);
      mru.current = mru.current.filter(x => x !== id);
      setState(prev => { const next = { ...prev }; delete next[id]; return next; });
    }
    known.current = ids;
  }, [servers, loadServer]);

  // The Core changed the rail (other device) or a node's status
  useRealtime('servers:updated', async (e) => {
    const before = serversRef.current.find(s => s.id === e?.node_id);
    await loadList();
    if (before) {
      known.current.delete(e.node_id); // load it again with the new status
      setServers(list => [...list]);
    }
  });
  useRealtime('dm:block', loadBlocks);
  useRealtime('resync', () => { loadList(); loadBlocks(); });

  // Mirror the BLOBL status to the servers (invisible shows as offline there)
  useEffect(() => {
    const status = user?.status_preference || 'online';
    statusRef.current = status;
    for (const c of conns.current.values()) c.setStatus(status);
  }, [user?.status_preference]);

  /* ── Events from the servers ── */

  const reloadUnreadSoon = useCallback((nodeId) => {
    clearTimeout(unreadReload.current[nodeId]);
    unreadReload.current[nodeId] = setTimeout(() => loadUnread(nodeId), 400);
  }, [loadUnread]);

  const setTyping = useCallback((nodeId, channelId, userId, on) => {
    const key = `${nodeId}:${channelId}:${userId}`;
    clearTimeout(typingTimers.current[key]);
    update(nodeId, st => ({ ...st, typing: { ...st.typing, [`${channelId}:${userId}`]: on } }));
    if (on) {
      typingTimers.current[key] = setTimeout(() => update(nodeId, st => ({ ...st, typing: { ...st.typing, [`${channelId}:${userId}`]: false } })), TYPING_TIMEOUT_MS);
    }
  }, [update]);

  handleEventRef.current = (nodeId, event, data) => {
    const me = meRef.current;
    switch (event) {
      case 'resync':
        loadServer(nodeId);
        break;
      case 'structure:updated':
        update(nodeId, st => ({ ...st, ...cleanStructure(data) }));
        break;
      case 'server:updated': {
        const info = cleanInfo(data);
        update(nodeId, st => ({ ...st, info: { ...info, id: st.info?.id || info.id } }));
        break;
      }
      case 'message:new': {
        const m = cleanMessage(data?.message);
        if (!m?.channel_id) break;
        const users = cleanUsers(data?.users);
        update(nodeId, st => {
          const u = st.unread[m.channel_id] || { last_message_id: null, last_read_id: 0, mentions: 0 };
          const next = { ...u, last_message_id: Math.max(u.last_message_id || 0, m.id) };
          if (m.author_id === me) next.last_read_id = Math.max(next.last_read_id, m.id);
          else if (m.mentions.includes(me)) next.mentions = (u.mentions || 0) + 1;
          return { ...st, unread: { ...st.unread, [m.channel_id]: next }, users: { ...st.users, ...users } };
        });
        if (m.author_id) setTyping(nodeId, m.channel_id, m.author_id, false);
        break;
      }
      case 'message:edited':
        update(nodeId, st => ({ ...st, users: { ...st.users, ...cleanUsers(data?.users) } }));
        break;
      case 'read:self': {
        const channelId = Number(data?.channel_id);
        const lastRead = Number(data?.last_read_id) || 0;
        let stale = false;
        update(nodeId, st => {
          const u = st.unread[channelId];
          if (!u) return st;
          const caughtUp = lastRead >= (u.last_message_id || 0);
          stale = !caughtUp && u.mentions > 0;
          return { ...st, unread: { ...st.unread, [channelId]: { ...u, last_read_id: Math.max(u.last_read_id, lastRead), mentions: caughtUp ? 0 : u.mentions } } };
        });
        if (stale) reloadUnreadSoon(nodeId);
        break;
      }
      case 'member:joined':
      case 'member:updated': {
        const m = cleanMember(data?.member);
        if (!m) break;
        update(nodeId, st => {
          const members = st.members && [...st.members.filter(x => x.id !== m.id), m];
          const meNext = m.id === me ? { ...st.me, role: m.role } : st.me;
          const ownerId = m.role === 'owner' ? m.id : (st.ownerId === m.id ? null : st.ownerId);
          const memberCount = event === 'member:joined' && st.info && !st.members?.some(x => x.id === m.id)
            ? st.info.member_count + 1 : st.info?.member_count;
          return { ...st, members, me: meNext, ownerId, users: { ...st.users, [m.id]: cleanUser(m) }, info: st.info && { ...st.info, member_count: memberCount } };
        });
        break;
      }
      case 'member:left':
      case 'user:deleted': {
        const id = Number(data?.user_id);
        update(nodeId, st => ({
          ...st,
          members: st.members && st.members.filter(x => x.id !== id),
          info: st.info && { ...st.info, member_count: Math.max(0, st.info.member_count - 1) },
          users: event === 'user:deleted' ? Object.fromEntries(Object.entries(st.users).filter(([k]) => Number(k) !== id)) : st.users,
        }));
        // The owner may have changed (succession after a deleted account)
        if (event === 'user:deleted') { ensureMembers(nodeId, true); loadServer(nodeId); }
        break;
      }
      case 'user:updated': {
        const u = cleanUser(data?.user);
        if (!u) break;
        update(nodeId, st => ({
          ...st,
          users: { ...st.users, [u.id]: u },
          members: st.members && st.members.map(m => (m.id === u.id ? { ...m, ...u } : m)),
        }));
        break;
      }
      case 'member:removed':
        update(nodeId, st => ({ ...st, status: 'error', error: { kind: 'not_member', message: REMOVED_TEXT[data?.reason] || 'You are not a member of this server anymore', reason: data?.reason } }));
        conns.current.get(nodeId)?.disconnect();
        break;
      case 'presence:snapshot': {
        const presence = {};
        for (const [k, v] of Object.entries(data || {})) if (['online', 'away', 'dnd'].includes(v)) presence[k] = v;
        update(nodeId, st => ({ ...st, presence }));
        break;
      }
      case 'presence:update': {
        const id = Number(data?.user_id);
        const status = ['online', 'away', 'dnd'].includes(data?.status) ? data.status : undefined;
        update(nodeId, st => {
          const presence = { ...st.presence };
          if (status) presence[id] = status; else delete presence[id];
          return { ...st, presence };
        });
        break;
      }
      case 'typing':
        if (Number(data?.user_id) && Number(data?.channel_id)) setTyping(nodeId, Number(data.channel_id), Number(data.user_id), !!data.typing);
        break;
      case 'timeout:updated':
        update(nodeId, st => ({ ...st, me: st.me && { ...st.me, timeoutUntil: validDate(data?.until) } }));
        break;
      case 'reports:updated':
        update(nodeId, st => ({ ...st, openReports: Number(data?.open) || 0 }));
        break;
      case 'auth:expired':
        break;
      default:
        break;
    }
    dispatch(nodeId, event, data);
  };

  /* ── Actions ── */

  // After joining through an invite: put it into the rail
  const addServer = useCallback(async (nodeId) => {
    await axios.post('/api/users/me/servers', { node_id: nodeId }, { headers: coreHeaders() });
    known.current.delete(nodeId);
    await loadList();
  }, [loadList]);

  const removeServer = useCallback(async (nodeId) => {
    try { await axios.delete(`/api/users/me/servers/${nodeId}`, { headers: coreHeaders() }); } catch {}
    setServers(list => list.filter(s => s.id !== nodeId));
  }, []);

  const leaveServer = useCallback(async (nodeId) => {
    await conn(nodeId)?.del('/api/members/me');
    await removeServer(nodeId);
  }, [conn, removeServer]);

  const reorder = useCallback((ids) => {
    setServers(list => ids.map(id => list.find(s => s.id === id)).filter(Boolean));
    axios.put('/api/users/me/servers/order', { node_ids: ids }, { headers: coreHeaders() }).catch(() => {});
  }, []);

  const setActiveChannel = useCallback((nodeId, channelId) => {
    active.current = nodeId ? { nodeId, channelId } : null;
  }, []);

  const on = useCallback((nodeId, event, handler) => {
    const key = `${nodeId}:${event}`;
    if (!listeners.current.has(key)) listeners.current.set(key, new Set());
    listeners.current.get(key).add(handler);
    return () => listeners.current.get(key)?.delete(handler);
  }, []);

  const value = useMemo(() => ({
    servers, listLoaded, state, blockedIds,
    conn, connFor, loadServer, loadUnread, ensureMembers, touch, addServer, removeServer, leaveServer, reorder,
    setActiveChannel, on, update,
  }), [servers, listLoaded, state, blockedIds, conn, connFor, loadServer, loadUnread, ensureMembers, touch, addServer, removeServer, leaveServer, reorder, setActiveChannel, on, update]);

  return <ServersContext.Provider value={value}>{children}</ServersContext.Provider>;
}

export const useServers = () => useContext(ServersContext);

// State of one server (EMPTY while unknown)
export function useServer(nodeId) {
  const ctx = useContext(ServersContext);
  return {
    node: ctx.servers.find(s => s.id === nodeId) || null,
    st: ctx.state[nodeId] || EMPTY,
  };
}

// Subscribe to events of one server for the lifetime of the component
export function useServerEvents(nodeId, events, handler) {
  const { on } = useContext(ServersContext);
  const ref = useRef(handler);
  ref.current = handler;
  const key = events.join('|');
  useEffect(() => {
    if (!nodeId) return undefined;
    const offs = key.split('|').map(ev => on(nodeId, ev, (data) => ref.current(ev, data)));
    return () => offs.forEach(off => off());
  }, [on, nodeId, key]);
}
